import { useCallback, useEffect, useRef, useState } from 'react'
import type { ChannelReply } from './useModeration'
import { vclog } from './vc-log'
import {
  endStop,
  isStopPending,
  nextDeadline,
  overdueStops,
  settleEndedShares,
  startStop,
  stopReplyFailure,
  type PendingShareStops,
  type StopShareFailure,
} from '../services/share-stop-requests'

type Args = {
  /** Sends the stop; resolves with the channel's answer. */
  send: (peerId: string) => Promise<ChannelReply> | undefined
  /** Every remote peer sharing right now. A stop is done once its peer leaves this. */
  sharingPeerIds: readonly string[]
  /** A stop that did not take. Called at most once per stop. */
  onFailed: (peerId: string, failure: StopShareFailure) => void
}

export type ShareStopRequests = {
  /** Stop this presenter's share. A no-op while a stop for them is pending. */
  stop: (peerId: string) => void
  isStopping: (peerId: string) => boolean
  /** Changes whenever a stop starts or settles, for memos that read `isStopping`. */
  pending: PendingShareStops
}

/**
 * Tracks moderator stops on other people's shares until each is settled — see
 * services/share-stop-requests for why.
 */
export function useShareStopRequests({ send, sharingPeerIds, onFailed }: Args): ShareStopRequests {
  const [pending, setPending] = useState<PendingShareStops>(() => new Map())
  // Read synchronously by `stop`: two clicks inside one frame both see the
  // state from before either, and would both send.
  const pendingRef = useRef(pending)
  const sendRef = useRef(send)
  sendRef.current = send
  const onFailedRef = useRef(onFailed)
  onFailedRef.current = onFailed
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const update = useCallback((next: PendingShareStops) => {
    if (next === pendingRef.current) return
    pendingRef.current = next
    setPending(next)
  }, [])

  const fail = useCallback(
    (peerId: string, failure: StopShareFailure) => {
      if (!isStopPending(pendingRef.current, peerId)) return
      update(endStop(pendingRef.current, peerId))
      vclog('screen', 'stopping a share failed', { peerId, failure })
      if (mountedRef.current) onFailedRef.current(peerId, failure)
    },
    [update],
  )

  const stop = useCallback(
    (peerId: string) => {
      if (isStopPending(pendingRef.current, peerId)) return
      update(startStop(pendingRef.current, peerId, Date.now()))
      vclog('screen', 'stopping a share', { peerId })
      void sendRef.current(peerId)?.then((reply) => {
        const failure = stopReplyFailure(reply)
        if (failure) fail(peerId, failure)
      })
    },
    [update, fail],
  )

  // Done: the share is gone. Also clears a stop sent for someone who was no
  // longer sharing by the time it was clicked: there is nothing to wait for.
  const sharingKey = sharingPeerIds.join(',')
  useEffect(() => {
    update(settleEndedShares(pendingRef.current, sharingKey ? sharingKey.split(',') : []))
  }, [sharingKey, pending, update])

  // Still up after the wait: say so, and give the button back. A wake that
  // finds nothing overdue (the wall clock moved) arms the timer again rather
  // than leaving the button stuck on "Stopping…".
  const [wake, setWake] = useState(0)
  useEffect(() => {
    const deadline = nextDeadline(pending)
    if (deadline === null) return
    const timer = setTimeout(() => {
      const overdue = overdueStops(pendingRef.current, Date.now())
      for (const peerId of overdue) fail(peerId, 'overdue')
      if (overdue.length === 0) setWake((n) => n + 1)
    }, Math.max(0, deadline - Date.now()))
    return () => clearTimeout(timer)
  }, [pending, fail, wake])

  const isStopping = useCallback((peerId: string) => isStopPending(pending, peerId), [pending])

  return { stop, isStopping, pending }
}
